import { apiCall, api } from './api'

// ========== TYPES ==========

export interface PropProviderServer {
  id: number
  server_name: string
  is_active: boolean
  display_order: number
}

export interface PropProviderSize {
  id: number
  size: number
  is_active: boolean
}

export interface PropProvider {
  id: number
  name: string
  is_active: boolean
  servers: PropProviderServer[]
  sizes: PropProviderSize[]
}

export interface PropAccount {
  id: number
  user_id: number
  name: string
  metaapi_account_id: string
  account_type: string
  login: string
  server: string
  prop_size: number
  provider: string | null
  current_phase: 'challenge' | 'real_pre_payout' | 'real_post_payout'
  is_active: boolean
  balance: number
  equity: number
  current_equity: number
  payout_ready: boolean
  payout_in_progress: boolean
  first_payout_done: boolean
  last_payout_date: string | null
  created_at: string
  updated_at: string
}

export interface BrokerAccount {
  id: number
  user_id: number
  name: string
  metaapi_account_id: string
  account_type: string
  login: string
  server: string
  provider: string | null
  is_active: boolean
  balance: number
  equity: number
  created_at: string
  updated_at: string
}

export interface HedgePair {
  id: number
  prop_account_id: number
  broker_account_id: number
  allocation_ratio: number
  is_active: boolean
  display_name: string | null
  created_at: string
}

export interface HedgePairWithNames extends HedgePair {
  prop_account_name?: string
  broker_account_name?: string
}

export interface HedgePairStatus {
  prop_status: string
  broker_status: string
  both_connected: boolean
}

export interface ConnectPropAccountData {
  login: string
  password: string
  provider_id: number  // ID-based referential integrity
  server_id: number    // ID-based referential integrity
  prop_size: number
  phase: string
}

export interface ConnectBrokerAccountData {
  login: string
  password: string
  server: string
  provider: string
}

export interface CreateHedgePairData {
  prop_account_id: number
  broker_account_id: number
}

export interface PropRealData {
  equity: number
  balance: number
  gold_price: number
  prop_lot: number
  broker_lot: number
  phase: string
  coeff: number
  margin_used: number
  max_margin: number
  status: string
  coverage_percent: number
  exposure_usd: number
  is_gold_price_fallback: boolean
  sl_dollar: number
  tp_dollar: number
  prop_size: number
}

export interface ManualTradeCalculateRequest {
  prop_account_id: number
  phase: string
  direction: string
  entry_price: number
  stop_loss: number
  take_profit: number
}

export interface SlTpCalculateResponse {
  prop_sl: number
  prop_tp: number
  broker_sl: number | null
  broker_tp: number | null
  current_gold_price: number
  prop_lot: number
  broker_lot: number | null
  sl_percent_used: number
  profit_needed: number
}

export interface ManualTradeCalculateResponse {
  // PROP data
  prop_name: string
  prop_equity: number
  prop_direction: string
  prop_lot: number
  prop_sl: number
  prop_tp: number
  // Broker data
  broker_name: string
  broker_equity: number
  broker_direction: string
  broker_lot: number
  broker_sl: number
  broker_tp: number
  // Shared data
  entry_price: number
}

// ========== API SERVICE ==========

// ========== AUTO TRADING TYPES ==========

export interface ActiveHedgePair {
  hedge_pair_id: number
  prop_account_id: number
  prop_account_name: string
  broker_account_id: number
  broker_account_name: string
  display_name: string
}

export interface AutoPreviewResponse {
  prop_account_id: number
  prop_account_name: string
  prop_equity: number
  broker_account_name: string
  broker_equity: number
  gold_price: number
  symbol: string
  prop_lot: number
  broker_lot: number
  coverage_percent: number
  prop_margin_required: number
  prop_margin_available: number
  broker_margin_required: number
  broker_margin_available: number
}

export interface ValidationResult {
  check_name: string
  passed: boolean
  message: string
  details?: Record<string, any>
}

export interface TradeData {
  account_id: number
  account_name: string
  account_type: string
  equity: number
  direction: string
  lot: number
  entry_price: number
  stop_loss: number
  take_profit: number
  margin_required: number
  margin_available: number
  margin_usage_percent: number
  coverage_percent?: number
}

export interface AutoExecuteResponse {
  success: boolean
  message: string
  can_execute: boolean
  validations: ValidationResult[]
  gold_price: number
  symbol: string
  prop_trade: TradeData
  broker_trade: TradeData
  warnings: string[]
  errors: string[]
}

export interface HedgeExecutionResult {
  success: boolean
  transaction_id: string
  prop_position_id: string
  prop_entry_price: number
  prop_lot: number
  prop_stop_loss: number
  prop_take_profit: number
  broker_position_id: string
  broker_entry_price: number
  broker_lot: number
  broker_stop_loss: number
  broker_take_profit: number
  price_diff: number
  slippage_status: string
  executed_at: string
  phase: string
  symbol: string
  prop_account_id?: number
  broker_account_id?: number
  coverage_percent?: number
}

export const accountsService = {
  // Prop Providers (Public)
  getPropProviders: () =>
    apiCall<PropProvider[]>('/accounts/prop-providers', {
      requiresAuth: false
    }),

  // Prop Accounts
  getPropAccounts: () =>
    apiCall<PropAccount[]>('/accounts/prop'),

  // Broker Accounts
  getBrokerAccounts: () =>
    apiCall<BrokerAccount[]>('/accounts/broker'),

  // Hedge Pairs
  getHedgePairs: () =>
    apiCall<HedgePair[]>('/accounts/hedge-pairs'),

  createHedgePair: (data: CreateHedgePairData) =>
    apiCall<HedgePair>('/accounts/hedge-pairs', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  deleteHedgePair: (pairId: number) =>
    apiCall<{ message: string }>(`/accounts/hedge-pairs/${pairId}`, {
      method: 'DELETE'
    }),

  updateHedgePairName: (pairId: number, displayName: string) =>
    apiCall<HedgePair>(`/accounts/hedge-pairs/${pairId}/name`, {
      method: 'PATCH',
      body: JSON.stringify({ display_name: displayName })
    }),

  toggleHedgePairActive: (pairId: number) =>
    apiCall<HedgePair>(`/accounts/hedge-pairs/${pairId}/toggle`, {
      method: 'PATCH'
    }),

  // Update prop phase
  updatePropPhase: (accountId: number, newPhase: string) =>
    apiCall<PropAccount>(`/accounts/prop/${accountId}/phase`, {
      method: 'PATCH',
      body: JSON.stringify({ phase: newPhase })
    }),

  // Automatic Trading
  getActiveHedgePairs: () =>
    apiCall<ActiveHedgePair[]>('/trading/active-hedge-pairs'),

  // Endpoint unificato: gold price + equity/margin + spread/point_size in una sola chiamata
  getBrokerInfo: (brokerAccountId: number) =>
    apiCall<{
      gold_price: number; broker_account_id: number; broker_account_name: string;
      broker_equity: number; broker_margin_free: number;
      spread_points: number; bid: number; ask: number; point_size: number;
      ea_status?: 'live' | 'stale' | 'offline';
      last_seen?: string;
      last_seen_age_sec?: number;
      account_currency?: string;
      source?: string;
    }>(`/trading/broker-info/${brokerAccountId}`),

  // Fase B (iXen Console EA-only): dati broker della coppia iXen System via telemetria EA.
  // Sostituisce getBrokerInfo per la iXen Console; preferito per qualsiasi nuovo consumer.
  getIxenPairInfo: (pairId: number) =>
    apiCall<{
      pair_id: number; pair_display_name: string; broker_login: string;
      gold_price: number;
      broker_equity: number; broker_margin_free: number;
      spread_points: number; bid: number; ask: number; point_size: number;
      ea_status?: 'live' | 'stale' | 'offline';
      last_seen?: string;
      last_seen_age_sec?: number;
      account_currency?: string;
      source?: string;
    }>(`/trading/ixen-pair-info/${pairId}`),

  // Get gold price and margin data from broker account (retrocompatibilità)
  getBrokerGoldPrice: (brokerAccountId: number) =>
    apiCall<{ gold_price: number; broker_account_id: number; broker_account_name: string; broker_equity?: number; broker_margin_free?: number }>(`/trading/broker-gold-price/${brokerAccountId}`),

  // SL Preference
  updateSlPreference: (propAccountId: number, slPercentage: number) =>
    apiCall<PropAccount>(`/accounts/prop/${propAccountId}/sl-preference`, {
      method: 'PATCH',
      body: JSON.stringify({ sl_percentage: slPercentage })
    }),

  // Calculator (no prop account connected)
  calculateTrading: (request: CalculatorRequest) =>
    apiCall<CalculatorResponse>('/trading/calculator', {
      method: 'POST',
      body: JSON.stringify(request)
    }),

  // Provider Access
  getProviderAccess: () =>
    apiCall<ProviderAccessListResponse>('/payments/providers/access'),

  purchaseProviderAccess: (providerCode: string) =>
    apiCall<ProviderPurchaseResponse>('/payments/providers/purchase', {
      method: 'POST',
      body: JSON.stringify({ provider_code: providerCode })
    }),

  confirmProviderPurchase: (providerCode: string, paymentIntentId: string) =>
    apiCall<{ success: boolean; message: string }>(`/payments/providers/confirm/${providerCode}?payment_intent_id=${paymentIntentId}`, {
      method: 'POST'
    })
}

// Validation Status Types (exported for use in components)
export interface ValidationCheck {
  status: string
  color: string
  count?: number
  percentage?: number
}

export interface AccountValidationStatus {
  connection: ValidationCheck
  positions: ValidationCheck
  margin: ValidationCheck
  target_profit?: ValidationCheck
  payout?: ValidationCheck
  trading_time?: ValidationCheck
}

export interface ValidationStatusResponse {
  hedge_pair_id: number
  prop: AccountValidationStatus
  broker: AccountValidationStatus
}

// Daily Loss Status Types
export interface DailyLossStatusResponse {
  scenario: number
  max_sl_pct: number
  max_sl_usd: number
  allowed: boolean
  reason: string
  daily_loss_usd: number
  daily_profit_usd: number
  daily_net_loss_usd: number
  daily_limit_usd: number
  remaining_daily_allowance_usd: number
  current_equity: number
  prop_size: number
  buffer_usd: number
  hedge_mode: 'FULL_HEDGE' | 'PROP_ONLY'
  preferred_sl_percentage: number
}

// Calculator Types (no prop account connected)
export interface CalculatorRequest {
  provider: string
  prop_equity: number
  broker_account_id?: number
  ixen_pair_id?: number
  phase: string
  direction: string
  entry_price: number
  sl_percent: number
  tp_percent?: number
  use_dynamic_spread?: boolean
  prop_spread_points?: number
  broker_spread_points?: number
  point_size?: number
  buffer_usd?: number
  funder_pro_margin_percent?: number
  fundednext_leverage?: number
  fp2_risk_profile?: 'conservativo' | 'aggressivo'
  gold_price?: number
  broker_equity?: number
  broker_margin_free?: number
}

export interface CalculatorResponse {
  provider: string
  prop_size_detected: number
  prop_equity: number
  broker_account_name: string
  broker_equity: number
  gold_price: number
  phase: string
  lot_prop: number
  lot_broker: number
  coverage_percent: number
  prop_direction: string
  prop_sl: number
  prop_tp: number
  broker_direction: string
  broker_sl: number
  broker_tp: number
  broker_margin_required: number
  broker_margin_available: number
  broker_can_trade: boolean
  warnings: string[]
  offset_mode?: string
  offset_price?: number
  prop_spread_points?: number
  broker_spread_points?: number
  point_size?: number
  buffer_points?: number
  offset_points?: number
}

export interface BrokerSpreadResponse {
  spread_points: number
  bid: number
  ask: number
  point_size: number
  timestamp?: string
}

// Provider Access Types
export interface ProviderAccess {
  provider_code: string
  provider_name: string
  is_default_included: boolean
  has_access: boolean
  one_time_price: number
  currency: string
  purchase_status: string | null
}

export interface ProviderAccessListResponse {
  providers: ProviderAccess[]
}

export interface ProviderPurchaseResponse {
  checkout_url: string
}
